import { motion } from "framer-motion"
import styled from "@emotion/styled"

import { usePage } from "contexts/Page"

import { headline100, surtitle100, quote70 } from "css/text"

import Image from "components/media/ImageCLD"
import Video, { cssVideo } from "components/media/VideoCLD"
import VideoLauncher from "components/video-launcher/VideoLauncher"
import { cssVars } from "components/nav-v2/Wrapper"
import ReadingInfo from "components/reading-info/ReadingInfo"
import Clock from "./clock/clock"

const Root = styled.header`
  --clock-offset: ${({ hasClock }) => (hasClock ? "-140px" : "0px")};
  --offset: 2px;

  align-items: end;
  display: grid;
  margin-block-start: 0;
  position: relative;

  > * {
    grid-area: 1 / 1;
  }

  img,
  picture,
  .${cssVideo} {
    display: block;
    height: calc(100svh - var(${cssVars.main.safe}) + var(--offset) + var(--clock-offset));
    object-fit: cover;
    object-position: center;
  }
`

const Content = styled.div`
  align-items: center;
  color: white;
  display: flex;
  flex-direction: column;
  justify-self: center;
  margin-block-end: 15vh;
  padding-inline: var(--outer-margin);
  position: relative;
  row-gap: 2.5rem;
  text-align: center;
  width: fit-content;
  z-index: 0;

  .cld-video-player {
    top: 0 !important;
  }

  /* a11y */
  html.prefers-contrast & {
    color: black !important;
    position: relative;

    ::after {
      background: white;
      content: "";
      height: 100%;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 20px);
      z-index: -1;
    }
  }
`

const Hgroup = styled.hgroup`
  display: flex;
  flex-direction: column;
  ${({ is_rftc }) => !is_rftc && "row-gap: 2ex;"}

  // hide video "pixel"
  position: relative;
  z-index: 1;
`

const H1 = styled(motion.h1)`
  ${({ is_rftc }) => (is_rftc ? surtitle100 : headline100)};
  ${({ is_rftc }) => is_rftc && "font-size: clamp(2.25rem, 1.1875rem + 2.6563vw, 4.375rem);"}
`

const P = styled(motion.p)`
  ${quote70}
  text-wrap: balance;
`

const CTA = styled(VideoLauncher)``

const transition = {
  duration: 0.3,
  ease: "easeOut",
  type: "tween",
}

const animation = {
  top: {
    show: { y: "0%", opacity: 1, transition },
    hide: { y: "100%", opacity: 0, transition },
  },
  bottom: {
    show: { y: "0%", opacity: 1, transition },
    hide: { y: "-100%", opacity: 0, transition },
  },
}

function Text({ title, subtitle, is_rftc, video_amp_cta }) {
  return (
    <Content>
      {is_rftc ? (
        <Hgroup is_rftc>
          <P initial='hide' animate='show' variants={animation.top}>
            <svg viewBox='0 0 207.1 224.8' xmlns='http://www.w3.org/2000/svg' fill='white' width='5rem' height='5rem' aria-hidden='true'>
              <path d='m62.8 164.5-43.5-93.1c4.2-2.2 7.1-6.6 7.1-11.7 0-7.3-5.9-13.2-13.2-13.2s-13.2 5.9-13.2 13.2 5.9 13.2 13.2 13.2 2.2-.2 3.3-.4l37.5 127.3c4.2 14.2 24.9 25 49.8 25s45.6-10.8 49.8-25l37.4-127.2c.9.2 1.9.3 2.9.3 7.3 0 13.2-5.9 13.2-13.2s-5.9-13.2-13.2-13.2-13.2 5.9-13.2 13.2 3.1 9.7 7.5 11.9l-43.4 93 7-125.3h.3c7.3 0 13.2-5.9 13.2-13.2s-5.9-13.2-13.2-13.2-13.2 5.9-13.2 13.2 4 11 9.5 12.6l-28.9 122.5-13.9-135c6.4-.9 11.3-6.4 11.3-13s-5.9-13.2-13.2-13.2-13.2 5.9-13.2 13.2 5 12.2 11.4 13l-13.9 134.8-28.8-122.2c5.5-1.6 9.6-6.6 9.6-12.7s-5.9-13.2-13.2-13.2-13.2 5.9-13.2 13.2 5.9 13.2 13.2 13.2h.2zm40.8 24.5c22.8 0 41.3 5.8 41.3 13s-18.5 13-41.3 13-41.3-5.8-41.3-13 18.5-13 41.3-13' />
            </svg>
          </P>
          <H1 is_rftc initial='hide' animate='show' variants={animation.bottom} dangerouslySetInnerHTML={{ __html: title }} />
        </Hgroup>
      ) : (
        <Hgroup>
          <H1 initial='hide' animate='show' variants={animation.top} dangerouslySetInnerHTML={{ __html: title }} />
          <P initial='hide' animate='show' variants={animation.bottom} dangerouslySetInnerHTML={{ __html: subtitle }} />
        </Hgroup>
      )}

      {video_amp_cta?.sources?.length > 0 && <CTA video_player_cld={video_amp_cta} className={video_amp_cta.style ?? "filled translucent-dark"} />}
    </Content>
  )
}

export default function Cover({ heading, media, text, video_amp_cta, clock }) {
  const { is_reading_info, reading_info } = usePage()

  const { video_cld, image_cld } = media?.[0] ?? {}

  return (
    <>
      <Root className='dark-theme' hasClock={Boolean(clock?.start_date)}>
        {image_cld && <Image loading='eager' {...image_cld} /*alt={asset?.alt ?? ""}*/ sizes='100vw' />}
        {video_cld && <Video autoPlay loop muted noButton {...video_cld} className={cssVideo} sizes='100vw' />}
        {text && <Text video_amp_cta={video_amp_cta?.[0]?.video_player_cld} {...heading} />}
      </Root>
      {is_reading_info ? <ReadingInfo {...reading_info} /> : null}
      {clock?.start_date && <Clock {...clock} />}
    </>
  )
}
